<script>
  import moment from "moment";
  import { stringify } from "query-string";
  import { navigate } from "svelte-routing";
  import { Head, Body, Row, Cell } from "@smui/data-table";
  import ResourceList from "../components/ResourceList.svelte";
  import { ResourceSelect, ResourceOption, Textfield } from "../components";
  import Checkbox from "@smui/checkbox";
  import FormField from "@smui/form-field";
  import { exclude } from "@smui/common/exclude.js";
  import Button, { Label, Icon } from "@smui/button";
  import { apiPost, apiGet } from "../httpCommon";

  export let title;
  export let location;
  export let basePath = "/sales";
  export let popupId = null;
  title = "Orders";

  let records;
  let list;
  let order_status = ["OPEN"];
  let filter = { online_order: true, order_status: order_status };
  let sort = ["order_no", "desc"];
  let loading;

  $: {
    if (popupId && filter.popup_id != popupId) {
      filter.popup_id = popupId;
      list && list.refresh();
    }
  }

  function statusFilterChanged(event) {
    filter.order_status = order_status;
    list.refresh();
  }

  $: props = exclude($$props, ["popupId", "title"]);

  function changeFilter(event) {
    if (!event.target.name) {
      console.log("Filter name not set");
    } else {
      console.log("Change filter: ", event.target.name, event);
      if (event.target.value) {
        filter[event.target.name] = event.target.value;
      } else {
        delete filter[event.target.name];
      }
      list.refresh();
    }
  }

  function refresh() {
    list.refresh();
  }

  function setSort(col) {
    const [sortCol, sortDir] = sort;
    if (col != sortCol) {
      sort = [col, "asc"];
    } else {
      sort = [col, sortDir === "asc" ? "desc" : "asc"];
    }
    console.log(`Sort now: ${sort}`);
    list.refresh();
  }

  let processing = {};
  const confirmActions = ["CANCEL", "REFUND"];
  async function processRowAction(row, action) {
    if (confirmActions.includes(action)) {
      const msg =
        `Are you sure you wish to ${action} order ${row["order_no"]}?` +
        `\n\nThis cannot be undone`;

      const proceed = window.confirm(msg);
      if (!proceed) {
        return;
      }
    }
    try {
      processing[row.id] = true;
      const resp = await apiPost(`${basePath}/${row.id}/process`, { action });
      replaceSale(resp);
    } catch (err) {
      console.log("Error", err);
      replaceSale(await apiGet(`${basePath}/${row.id}`));
    } finally {
      processing[row.id] = undefined;
    }
  }

  function replaceSale(sale) {
    if (sale && sale.id) {
      const index = records.findIndex(row => row.id == sale.id);
      if (index >= 0) {
        records.splice(index, 1, sale);
        records = records; // force rerender
      }
    }
  }
</script>

<style>
  :global(tr.canceled td) {
    text-decoration: line-through;
    color: grey;
  }

  :global(tr.completed td) {
    font-weight: 600;
  }

  .discount {
    color: green;
  }
</style>

{(console.log('OrderList.filter: ', filter), '')}
<ResourceList
  resource="sales"
  pageSize={50}
  bind:this={list}
  bind:loading
  loadOnMount={!!popupId}
  {location}
  bind:records
  {filter}
  {sort}
  {...props}>
  <div slot="filters" class="flex wrap no-spread">
    <ResourceSelect
      input$name="popup_id"
      on:change={changeFilter}
      bind:value={popupId}
      resource="popups"
      selectFirst={true}
      filter={{ status: ['scheduled'] }}
      sort={['start_time', 'asc']}
      nameField={row => `${moment(row.start_time).format('Do MMM')} - ${row.vendor_name} @ ${row.venue_name}`}
      label="Event" />
    <div class="flex wrap">
      {#each ['OPEN', 'COMPLETED', 'CANCELED'] as status}
        <FormField>
          <Checkbox
            bind:group={order_status}
            value={status}
            on:change={statusFilterChanged} />
          <span slot="label">{status}</span>
        </FormField>
      {/each}
    </div>
    &nbsp;
    <!-- Textfield
      input$name="popup_date"
      type="date"
      value={filter.popup_date}
      on:change={changeFilter}
      label="Popup Date" / -->
  </div>
  <span slot="actions" let:loading>
    <Button on:click={refresh} class="create-button" disabled={loading}>
      <Icon class="material-icons">replay</Icon>
      <Label>refresh</Label>
    </Button>
  </span>
  <Head>
    <Row>
      <Cell class="pointer" on:click={() => setSort('sale_at')}>
        Order Time
      </Cell>
      <Cell class="pointer" on:click={() => setSort('order_no')}>
        Order No
        <br />
        Status
      </Cell>
      <Cell class="right">
        Amount
        <br />
        <span class="discount">discount</span>
      </Cell>
      <Cell>Payment</Cell>
      <Cell class="pointer" on:click={() => setSort('pickup_time')}>
        Pickup Time
        <br />
        Customer Name
      </Cell>
      <Cell>Pickup</Cell>
    </Row>
  </Head>
  <Body>
    {#if records && records.length}
      {#each records as row}
        <Row class={row.order_status.toLowerCase()}>
          <Cell>
            {moment(row.sale_at).format('Do MMM')}
            <br />
            {moment(row.sale_at).format('HH:mm')}
          </Cell>
          <Cell>
            {row.order_no || '-'}
            <br />
            {row.order_status || '-'}
          </Cell>
          <Cell class="right">
            {row.total_amount}
            {#if row.discount_amount > 0}
              <br />
              <span class="discount">
                {row.discount_code} : {row.discount_amount}
              </span>
            {/if}
          </Cell>
          <Cell>
            {#if row.order_actions && row.order_actions.length}
              {#each row.order_actions as action}
                <Button
                  variant="outlined"
                  color={confirmActions.includes(action) ? 'secondary' : 'primary'}
                  on:click={() => processRowAction(row, action)}
                  disabled={loading || processing[row.id]}>
                  <Label>{action}</Label>
                </Button>
                &nbsp;
              {/each}
            {:else}
              {row.payment_status}
            {/if}
          </Cell>
          <Cell>
            {moment(row.pickup_time).format('HH:mm')}
            <br />
            {row.customer_name || ''}
          </Cell>
          <Cell>
            {#if row.pickup_actions}
              {#each row.pickup_actions as action}
                <Button
                  variant="unelevated"
                  on:click={() => processRowAction(row, action)}
                  disabled={loading || processing[row.id]}>
                  <Label>{action}</Label>
                </Button>
              {/each}
            {/if}
          </Cell>
        </Row>
      {/each}
    {:else if loading}
      <h3>Loading....</h3>
    {:else}
      <h3>No records found</h3>
    {/if}
  </Body>
</ResourceList>
